import type { ReactNode } from 'react';
import { Box, HStack, SimpleGrid, Text, useRecipe } from '@chakra-ui/react';
import { optionTileRecipe } from '../../theme/recipes/option-tile';

/**
 * The single-select primitive behind match settings, history filters, and event
 * setup. `RiftboundSegmentedControl` covers short label-only choices; this is for
 * options that need helper text, a preview, or a per-option disabled state.
 */

export type OptionTileShape = 'tile' | 'pill';

export interface OptionTileProps {
  selected: boolean;
  label: ReactNode;
  helper?: ReactNode;
  /** Trailing micro-label, e.g. an event's organised-play level. */
  badge?: ReactNode;
  /** Block rendered above the label, e.g. the match layout thumbnails. */
  preview?: ReactNode;
  disabled?: boolean;
  shape?: OptionTileShape;
  /** Lets a tile stretch inside an HStack rail instead of a grid cell. */
  flex?: string;
  onClick: () => void;
}

export function OptionTile({
  selected,
  label,
  helper,
  badge,
  preview,
  disabled,
  shape = 'tile',
  flex,
  onClick,
}: OptionTileProps) {
  const isPill = shape === 'pill';
  const recipe = useRecipe({ recipe: optionTileRecipe });

  return (
    <Box
      as="button"
      aria-pressed={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
      onClick={disabled ? undefined : onClick}
      flex={flex}
      minW={flex ? 0 : undefined}
      css={recipe({ shape, selected })}
      opacity={disabled ? 0.52 : 1}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      _hover={disabled ? undefined : { borderColor: 'accentMuted' }}
    >
      {isPill ? label : (
        <>
          {preview ? <Box mb={2}>{preview}</Box> : null}
          {badge ? (
            <HStack justify="space-between" align="flex-start" gap={2}>
              <Text fontWeight="black" lineHeight="1.1">{label}</Text>
              <Text
                fontSize="3xs"
                fontWeight="bold"
                letterSpacing="0.1em"
                textTransform="uppercase"
                color="textMuted"
                flexShrink={0}
                mt={0.5}
              >
                {badge}
              </Text>
            </HStack>
          ) : (
            <Text fontWeight="black" lineHeight="1.1">{label}</Text>
          )}
          {helper ? (
            <Text fontSize="2xs" color="textMuted" mt={1} lineClamp={2}>
              {helper}
            </Text>
          ) : null}
        </>
      )}
    </Box>
  );
}

export interface OptionTileChoice<T> {
  value: T;
  label: ReactNode;
  helper?: ReactNode;
  badge?: ReactNode;
  preview?: ReactNode;
  disabled?: boolean;
}

export interface OptionTileGroupProps<T> {
  options: readonly OptionTileChoice<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Disables every option — settings locked mid-match, for example. */
  disabled?: boolean;
  shape?: OptionTileShape;
  columns?: number | Record<string, number>;
  /** Compares against `value`; supply when options carry object values. */
  isSelected?: (optionValue: T, value: T) => boolean;
}

/** Grid (or wrapping row, for pills) of `OptionTile`s bound to a single value. */
export function OptionTileGroup<T>({
  options,
  value,
  onChange,
  disabled,
  shape = 'tile',
  columns,
  isSelected,
}: OptionTileGroupProps<T>) {
  const tiles = options.map((option) => (
    <OptionTile
      key={String(option.value)}
      selected={isSelected ? isSelected(option.value, value) : option.value === value}
      label={option.label}
      helper={option.helper}
      badge={option.badge}
      preview={option.preview}
      disabled={disabled || option.disabled}
      shape={shape}
      onClick={() => onChange(option.value)}
    />
  ));

  if (shape === 'pill') {
    return <HStack gap={2} wrap="wrap">{tiles}</HStack>;
  }

  return (
    <SimpleGrid columns={columns ?? { base: 2, sm: 3 }} gap={2}>
      {tiles}
    </SimpleGrid>
  );
}
